<template>
  <div class="flex flex-col gap-[32px] mt-20" @click="clearErrorOnOutsideClick">
    <!-- Heading Section -->
    <PageHeading
      title="Collaboration & Adding Team Members"
      subtitle="Easily add team members to enhance collaboration and achieve your goals together."
    />

    <div class="max-w-lg flex flex-col gap-[20px] h-[calc(100vh-420px)] overflow-y-auto pr-4">
      <!-- Heading for Team Members -->
      <h2
        class="text-[#121212] font-semibold text-[20px] leading-[28px]"
        style="font-family: 'Open Sans'; font-weight: 600;"
      >
        Team Members
      </h2>

      <!-- Email Tags Input -->
      <EmailTagsInput
        v-model="form.emails"
        label="Emails"
        required
        :errorMessage="getErrorMessage"
        @input="v$.emails.$touch()"
        @on-tags-changed="handleEmailsChange"
        @click.stop
      />

      <!-- Display invitation message -->
      <transition name="fade">
        <p v-if="inviteMessage" 
           :class="inviteMessage === 'Invitation Sent' ? 'text-green-500' : 'text-red-500'"
           class="font-minion text-sm mt-2">
          {{ inviteMessage }}
        </p>
      </transition>

      <!-- Button Group -->
      <div class="w-full flex items-center justify-center gap-4">
        <!-- Skip Button -->
        <button
          class="w-full h-[48px] bg-white border-2 border-[#00423D] text-[#00423D] font-semibold text-[16px] leading-[24px] rounded-[12px] px-6 py-3 hover:bg-gray-50"
          style="font-family: 'Open Sans'; font-weight: 600;"          
          @click="handleSkip"
          :disabled="isSendingRequest"
        >
          Skip for Now
        </button>

        <!-- Submit Button -->
        <button
          class="w-full h-[48px] bg-[#00423D] text-white font-semibold text-[16px] leading-[24px] rounded-[12px] px-6 py-3"
          style="font-family: 'Open Sans'; font-weight: 600;"          
          @click="handleInviteButton"
          :disabled="isSendingRequest"
          ref="submitBtn"
        >
          <transition name="button-switch" mode="out-in">
            <span v-if="!isSendingRequest">Next</span>
            <div
              v-else
              class="max-h-6 overflow-hidden inline-flex items-center justify-center pt-3"
            >
              <Vue3Lottie
                :animationData="buttonAnimation"
                :height="120"
                :width="120"
              />
            </div>
          </transition>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import PageHeading from "@/components/PageHeading.vue";
import EmailTagsInput from "@/components/EmailTagsInput.vue";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";
import { useOnboardingStore } from '@/stores/onboarding';
// Import Vue3Lottie and animation
import { Vue3Lottie } from "vue3-lottie";
import ButtonAni from "@/assets/loading-button-simple.json";

const router = useRouter();
const authStore = useAuthStore();
const submitBtn = ref(null);
const buttonAnimation = ref(ButtonAni);
const onboardingStore = useOnboardingStore();

const form = ref({
  emails: [],
});

const inviteMessage = ref("");
const isSendingRequest = ref(false);
const hasFilledEmails = ref(null);
const showError = ref(false);

// Validation rules
const rules = computed(() => ({
  emails: {
    required: helpers.withMessage(
      "At least one email is required!",
      (value) => value.length > 0
    ),
  },
}));

const v$ = useVuelidate(rules, form);

const getErrorMessage = computed(() => {
  if (!showError.value) return "";
  
  if (v$.value.emails.$error) {
    return v$.value.emails.$errors[0].$message;
  }
  return hasFilledEmails.value === false ? "At least one email is required!" : "";
});

// Handle email changes
function handleEmailsChange(emails) {
  hasFilledEmails.value = true;
  form.value.emails = emails;
  showError.value = true;
  v$.value.emails.$touch();
}

// Clear error message when clicking outside the email input
function clearErrorOnOutsideClick() {
  if (form.value.emails.length > 0) {
    showError.value = false;
  }
}

// Handle invite button click
async function handleInviteButton() {
  showError.value = true;
  v$.value.emails.$touch();

  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }

  isSendingRequest.value = true;
  if (submitBtn.value) submitBtn.value.disabled = true;
  
  try {
    const res = await fetch(`${global.API_URL}/users/invite`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ emails: form.value.emails }),
    });
    
    const data = await res.json();
    
    if (!data.data) {
      // Success case
      inviteMessage.value = "Invitation Sent";
      form.value.emails = [];
      
      // Update onboarding status
      onboardingStore.batchUpdateRouteStatus({
        updates: [{ routeName: '/onboarding/team', newRouteState: 1 }]
      });
      
      // Wait for 2 seconds to show success message, then redirect
      setTimeout(() => {
        router.push({ name: "getting-started-welcome" });
      }, 2000);
    } else {
      // Error from API
      inviteMessage.value = data.data;
      setTimeout(() => {
        inviteMessage.value = "";
        isSendingRequest.value = false;
        if (submitBtn.value) submitBtn.value.disabled = false;
      }, 3000);
    }
  } catch (err) {
    console.error("Error sending invitations:", err);
    inviteMessage.value = "Failed to send invitations. Please try again.";
    setTimeout(() => {
      inviteMessage.value = "";
      isSendingRequest.value = false;
      if (submitBtn.value) submitBtn.value.disabled = false;
    }, 3000);
  }
}

// Handle skip button click
async function handleSkip() {
  // Update onboarding status
  await onboardingStore.batchUpdateRouteStatus({
    updates: [{ routeName: '/onboarding/team', newRouteState: 1 }]
  });
  
  // Redirect to getting-started-welcome
  router.push({ name: "getting-started-welcome" });
}

// Load component
onMounted(() => {
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Button animation transitions */
.button-switch-enter-active,
.button-switch-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.4s ease-in-out;
}

.button-switch-enter-from {
  opacity: 0;
  transform: translateX(-100px);
}

.button-switch-leave-to {
  opacity: 0;
  transform: translateX(100px);
}
</style>